import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "sonner";
import { AxiosError } from "axios";
import apiService from "../../services/api.services";

// Types
export interface User {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  role: string;
  phone_number?: string;
  country?: string;
  created_at: string | Date;
  course_title?: string;
}

export interface UserState {
  users: Record<number, User>;
  tutors: Record<number, any>;
  students: Record<number, any>;
  currentUser: User | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  total: number;
  currentPage: number;
  lastFetched: Record<string, number>;
}



const initialState: UserState = {
  users: {},
  tutors: {},
  students: {},
  currentUser: null,
  status: "idle",
  error: null,
  total: 0,
  currentPage: 1,
  lastFetched: {},
};

// Async Thunks
export const fetchTutors = createAsyncThunk(
  "users/fetchTutors",
  async (
    params: { page?: number; limit?: number } = {},
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams({
        page: String(params.page || 1),
        limit: String(params.limit || 10),
      });

      const response = await apiService.get<{
        tutors: User[];
        total: number;
        page: number;
      }>(`/users/tutors?${queryParams}`, { withCredentials: true });

      return {
        tutors: response.tutors,
        total: response.total,
        currentPage: response.page,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to fetch tutors"
      );
      return rejectWithValue({
        message: axiosError.response?.data?.message || "Failed to fetch tutors",
      });
    }
  }
);

export const fetchStudentProfile = createAsyncThunk<any, any>(
  "users/fetchStudentProfile",
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await apiService.get(
        `/users/student-profile/${studentId}`
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to fetch profile"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to fetch profile",
      });
    }
  }
);

export const fetchTutorProfile = createAsyncThunk<any, any>(
  "users/fetchTutorProfile",
  async (tutorId, { rejectWithValue }) => {
    try {
      const response = await apiService.get<{ data: User }>(
        `/users/tutor-profile/${tutorId}`
      );

      return response;
    } catch (error) {

      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to fetch profile"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to fetch profile",
      });
    }
  }
);

export const updateStudentProfile = createAsyncThunk(
  "users/updateStudentProfile",
  async (profileData: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await apiService.put<{ profile: User }>(
        `/users/student/profile`,
        profileData
      );

      toast.success("Profile updated successfully");
      return response.profile;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to update profile"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to update profile",
      });
    }
  }
);

export const fetchTutorStudents = createAsyncThunk(
  "users/fetchTutorStudents",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get<{ students: User[] }>(
        `/users/tutor/students`
      );

      return response.students;
    } catch (error) {

      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to fetch students"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to fetch students",
      });
    }
  }
);

export const fetchOrganizationStudents = createAsyncThunk(
  "users/fetchOrganizationStudents",
  async (
    params: {
      organizationId: number;
      page?: number;
      limit?: number
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams({
        page: String(params.page || 1),
        limit: String(params.limit || 10),
      });

      const response = await apiService.get<{
        students: User[];
        total: number;
        page: number;
        totalPages: number;
      }>(`/users/organization/${params.organizationId}/students?${queryParams}`);

      // Return the full response to handle pagination data
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to fetch students"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to fetch students",
      });
    }
  }
);


const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    resetUsers: (state) => {
      state.users = {};
      state.tutors = {};
      state.students = {};
      state.currentUser = null;
      state.status = "idle";
      state.error = null;
      state.total = 0;
      state.currentPage = 1;
      state.lastFetched = {};
    },
    setCurrentUser: (state, action: PayloadAction<User>) => {
      state.currentUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Tutors Fetch
      .addCase(fetchTutors.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchTutors.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.tutors = action.payload.tutors.reduce(
          (acc: Record<number, User>, tutor: User): Record<number, User> => ({
            ...acc,
            [tutor.id]: tutor,
          }),
          {} as Record<number, User>
        );
        state.total = action.payload.total;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchTutors.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          (action.payload as { message: string })?.message ||
          "Failed to fetch tutors";
      })

      // Student Profile
      .addCase(fetchStudentProfile.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchStudentProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { studentProfile } = action.payload;
        state.currentUser = studentProfile;
        state.students[studentProfile.id] = studentProfile;
      })
      .addCase(fetchStudentProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as any)?.message || "Failed to fetch profile";
      })

      // Tutor Profile
      .addCase(fetchTutorProfile.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchTutorProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { tutorProfile } = action.payload;
        state.currentUser = tutorProfile;
        state.tutors[tutorProfile.id] = tutorProfile;
      })
      .addCase(fetchTutorProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as any)?.message || "Failed to fetch profile";
      })

      // Update Student Profile
      .addCase(updateStudentProfile.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        state.students[action.payload.id] = action.payload;
      })

      // Tutor Students
      .addCase(fetchTutorStudents.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchTutorStudents.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.students = (action.payload as User[]).reduce(
          (acc: Record<number, User>, student: User) => ({
            ...acc,
            [student.id]: student,
          }),
          {}
        );
      })
      .addCase(fetchOrganizationStudents.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOrganizationStudents.fulfilled, (state, action) => {
        state.status = "succeeded";

        // Now action.payload has the full response structure
        const { students, total, page } = action.payload;

        if (Array.isArray(students)) {
          state.students = students.reduce(
            (acc: Record<number, User>, student: User) => ({
              ...acc,
              [student.id]: student,
            }),
            {}
          );
          // Also update pagination info if needed
          state.total = total;
          state.currentPage = page;
        } else {
          console.error('Expected students array but received:', students);
          state.students = {};
        }
      });

  },
});

// Selectors
export const selectTutors = createSelector(
  [(state: { users: UserState }) => state.users.tutors],
  (tutors) => Object.values(tutors)
);

export const selectStudents = createSelector(
  [(state: { users: UserState }) => state.users.students],
  (students) => Object.values(students)
);

//select organization students where organization_id is not null
export const selectOrganizationStudents = createSelector(
  [(state: { users: UserState }) => state.users.students],
  (students) =>
    Object.values(students).filter((student) => student.organization_id)
);

export const selectCurrentUser = createSelector(
  [(state: { users: UserState }) => state.users],
  (users) => users.currentUser
);

export const selectTutorById = createSelector(
  [(state: { users: UserState }) => state.users.tutors, (_, id: number) => id],
  (tutors, id) => tutors[id]
);

export const selectStudentById = createSelector(
  [
    (state: { users: UserState }) => state.users.students,
    (_, id: number) => id,
  ],
  (students, id) => students[id]
);

export const { resetUsers, setCurrentUser } = usersSlice.actions;

export default usersSlice.reducer;
